import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import CreditCardTwoToneIcon from "@mui/icons-material/CreditCardTwoTone";
import PaidTwoToneIcon from "@mui/icons-material/PaidTwoTone";
import PercentTwoToneIcon from "@mui/icons-material/PercentTwoTone";
import TuneTwoToneIcon from "@mui/icons-material/TuneTwoTone";
import { LoadingButton } from "@mui/lab";
import {
	Alert,
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Container,
	InputAdornment,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import GlobalLoader from "src/components/SuspenseLoader/GlobalLoader";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import type {
	ChargeType,
	ITicketConfig,
	ITicketConfigPayload,
} from "../type/ticketConfigType";

interface FormValues {
	price: string | number;
	charge_type: ChargeType;
	payment_gateway_charge: string | number;
}

const TICKET_PRESETS = [0, 100, 150, 200, 250, 300, 500];

const TicketConfiguration: React.FC = () => {
	const theme = useTheme();
	const [savedSuccess, setSavedSuccess] = useState<boolean>(false);

	const { data: rawData, isLoading } = useApiQuery<
		ITicketConfig | ITicketConfig[]
	>({
		endPoint: "/api/v1/admin/visitor/ticket-config",
		queryKeys: ["TicketConfig"],
	});

	const config: ITicketConfig | null = rawData
		? Array.isArray(rawData)
			? rawData[0] || null
			: rawData
		: null;

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			price: "",
			charge_type: "percentage",
			payment_gateway_charge: "",
		},
	});

	const watchedPrice = watch("price");
	const watchedChargeType = watch("charge_type") || "percentage";
	const watchedCharge = watch("payment_gateway_charge");

	useEffect(() => {
		if (config) {
			reset({
				price: Number(config.price) || 0,
				charge_type: (config.charge_type as ChargeType) || "percentage",
				payment_gateway_charge:
					Number(
						config.payment_gateway_charge ??
						config.charge ??
						config.online_charge ??
						0
					) || 0,
			});
		}
	}, [config, reset]);

	const { mutate: updateTicketConfig, isPending } = useApiMutation<
		ITicketConfigPayload,
		any
	>({
		method: "POST",
		endPoint: "/api/v1/admin/visitor/ticket-config",
		invalidateQueries: ["TicketConfig"],
	});

	const onSubmit = (values: FormValues) => {
		const priceNum = Number(values.price);
		const chargeNum = Number(values.payment_gateway_charge) || 0;
		if (isNaN(priceNum) || priceNum < 0) return;
		if (isNaN(chargeNum) || chargeNum < 0) return;

		updateTicketConfig(
			{
				price: priceNum,
				charge_type: values.charge_type,
				payment_gateway_charge: chargeNum,
			},
			{
				onSuccess: () => {
					setSavedSuccess(true);
					setTimeout(() => setSavedSuccess(false), 4000);
				},
			}
		);
	};

	if (isLoading && !config) {
		return <GlobalLoader />;
	}

	const currentPrice = Number(config?.price || 0);
	const currentChargeType: ChargeType =
		(config?.charge_type as ChargeType) || "percentage";
	const currentCharge = Number(
		config?.payment_gateway_charge ?? config?.charge ?? config?.online_charge ?? 0
	);

	return (
		<>
			<Helmet>
				<title>BTTF - Ticket Configuration</title>
			</Helmet>

			<Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
				{/* Page Header */}
				<Box sx={{ mb: 2.5, maxWidth: 540, mx: "auto" }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
						<Box
							sx={{
								p: 0.8,
								borderRadius: 1.5,
								bgcolor: alpha(theme.palette.primary.main, 0.1),
								color: "primary.main",
								display: "flex",
							}}
						>
							<ConfirmationNumberTwoToneIcon sx={{ fontSize: 24 }} />
						</Box>
						<Box>
							<Typography variant="h3" fontWeight={700}>
								Ticket Price & Gateway Configuration
							</Typography>
						</Box>
					</Box>
				</Box>

				{/* Single Compact Card */}
				<Card
					elevation={2}
					sx={{
						maxWidth: 540,
						mx: "auto",
						borderRadius: 2,
						overflow: "hidden",
					}}
				>
					<CardHeader
						avatar={
							<Box
								sx={{
									p: 0.6,
									borderRadius: 1.5,
									bgcolor: alpha(theme.palette.secondary.main, 0.1),
									color: "secondary.main",
									display: "flex",
								}}
							>
								<TuneTwoToneIcon fontSize="small" />
							</Box>
						}
						title={
							<Typography variant="h4" fontWeight={700}>
								Update Configuration
							</Typography>
						}
						sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
					/>

					<CardContent sx={{ p: 2.5 }}>
						{savedSuccess && (
							<Alert
								severity="success"
								icon={<CheckCircleTwoToneIcon fontSize="small" />}
								sx={{
									mb: 2,
									borderRadius: 1.5,
									py: 0.5,
									px: 2,
									fontWeight: 600,
									fontSize: "0.85rem",
								}}
								onClose={() => setSavedSuccess(false)}
							>
								Ticket configuration updated successfully! (Price: <strong>৳{watchedPrice}</strong>, Type: <strong>{watchedChargeType}</strong>, Charge: <strong>{watchedCharge || 0}{watchedChargeType === "percentage" ? "%" : " BDT"}</strong>)
							</Alert>
						)}

						<Box
							component="form"
							onSubmit={(e) => {
								e.preventDefault();
								handleSubmit(onSubmit)();
							}}
						>
							{/* Ticket Price Section */}
							<Box sx={{ mb: 3 }}>
								<Typography
									variant="h6"
									fontWeight={700}
									color="text.secondary"
									letterSpacing={0.5}
									sx={{ display: "block", mb: 0.8, fontSize: "0.72rem" }}
								>
									Ticket Price Presets
								</Typography>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 1.5 }}>
									{TICKET_PRESETS.map((preset) => {
										const isSelected = Number(watchedPrice) === preset;
										return (
											<Chip
												key={preset}
												label={preset === 0 ? "Free (৳0)" : `৳${preset}`}
												size="small"
												color={isSelected ? "primary" : "default"}
												variant={isSelected ? "filled" : "outlined"}
												onClick={() =>
													setValue("price", preset, { shouldValidate: true })
												}
												clickable
												sx={{
													fontWeight: isSelected ? 700 : 500,
													borderRadius: 1.5,
													height: 26,
													fontSize: "0.78rem",
													px: 0.2,
													transition: "all 0.15s ease",
													"&:hover": {
														borderColor: "primary.main",
													},
												}}
											/>
										);
									})}
								</Box>

								<Typography
									variant="h6"
									fontWeight={700}
									color="text.secondary"
									letterSpacing={0.5}
									sx={{ display: "block", mb: 0.6, fontSize: "0.72rem" }}
								>
									Ticket Price (BDT)
								</Typography>
								<TextField
									fullWidth
									size="small"
									type="number"
									placeholder="e.g. 250"
									inputProps={{ min: 0, step: "1" }}
									{...register("price", {
										required: "Ticket price is required",
										min: {
											value: 0,
											message: "Price cannot be negative",
										},
									})}
									error={Boolean(errors.price)}
									helperText={
										errors.price?.message ||
										"Enter 0 if admission to the fair is free."
									}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<PaidTwoToneIcon color="primary" fontSize="small" />
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<Typography
													variant="h6"
													fontWeight={700}
													color="text.secondary"
												>
													BDT
												</Typography>
											</InputAdornment>
										),
									}}
									sx={{
										"& input": {
											fontSize: "0.95rem",
											fontWeight: 600,
											letterSpacing: 0.5,
										},
									}}
								/>
							</Box>

							{/* Online Gateway Charged Amount Section */}
							<Box sx={{ mb: 2.5 }}>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 0.8,
									}}
								>
									<Typography
										variant="h6"
										fontWeight={700}
										color="text.secondary"
										letterSpacing={0.5}
										sx={{ fontSize: "0.72rem" }}
									>
										Online Gateway Charge
									</Typography>

									{/* Charge Type Selector */}
									<ToggleButtonGroup
										size="small"
										value={watchedChargeType}
										exclusive
										onChange={(_, newType: ChargeType | null) => {
											if (newType) {
												setValue("charge_type", newType, { shouldValidate: true });
											}
										}}
										sx={{
											p: "2.5px",
											bgcolor: alpha(theme.palette.primary.main, 0.08),
											borderRadius: "20px",
											border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
											"& .MuiToggleButtonGroup-grouped": {
												border: "none",
												borderRadius: "16px !important",
												px: 1.5,
												py: 0.35,
												height: 26,
												fontSize: "0.75rem",
												fontWeight: 600,
												textTransform: "none",
												color: "text.secondary",
												transition: "all 0.2s ease-in-out",
												"&.Mui-selected": {
													bgcolor: "primary.main",
													color: "#ffffff",
													fontWeight: 700,
													boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.35)}`,
													"&:hover": {
														bgcolor: "primary.dark",
													},
												},
												"&:not(.Mui-selected):hover": {
													bgcolor: alpha(theme.palette.primary.main, 0.12),
													color: "primary.main",
												},
											},
										}}
									>
										<ToggleButton value="percentage">
											<PercentTwoToneIcon sx={{ fontSize: 13, mr: 0.5 }} />
											Percentage (%)
										</ToggleButton>
										<ToggleButton value="fixed">
											<PaidTwoToneIcon sx={{ fontSize: 13, mr: 0.5 }} />
											Fixed (BDT)
										</ToggleButton>
									</ToggleButtonGroup>
								</Box>

								<TextField
									fullWidth
									size="small"
									type="number"
									placeholder={
										watchedChargeType === "percentage" ? "e.g. 2.5" : "e.g. 15"
									}
									inputProps={{
										min: 0,
										max: watchedChargeType === "percentage" ? 100 : undefined,
										step: "any",
									}}
									{...register("payment_gateway_charge", {
										min: {
											value: 0,
											message: "Gateway charge cannot be negative",
										},
										max:
											watchedChargeType === "percentage"
												? {
													value: 100,
													message: "Percentage charge cannot exceed 100%",
												}
												: undefined,
									})}
									error={Boolean(errors.payment_gateway_charge)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												{watchedChargeType === "percentage" ? (
													<PercentTwoToneIcon
														color="secondary"
														fontSize="small"
													/>
												) : (
													<CreditCardTwoToneIcon
														color="secondary"
														fontSize="small"
													/>
												)}
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<Typography
													variant="h6"
													fontWeight={700}
													color="text.secondary"
												>
													{watchedChargeType === "percentage" ? "%" : "BDT"}
												</Typography>
											</InputAdornment>
										),
									}}
									sx={{
										"& input": {
											fontSize: "0.95rem",
											fontWeight: 600,
											letterSpacing: 0.5,
										},
									}}
								/>

								{/* Calculation preview / breakdown */}
								{Number(watchedPrice) > 0 && Number(watchedCharge) > 0 && (
									<Box
										sx={{
											mt: 1.2,
											p: 1.2,
											borderRadius: 1.5,
											bgcolor: alpha(theme.palette.secondary.main, 0.05),
											border: `1px dashed ${alpha(theme.palette.secondary.main, 0.25)}`,
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ fontSize: "0.75rem", fontWeight: 600 }}
										>
											Estimated Total per Online Ticket:
										</Typography>
										<Typography
											variant="subtitle2"
											fontWeight={700}
											color="secondary.main"
											sx={{ fontSize: "0.82rem" }}
										>
											৳{watchedPrice} + ৳
											{watchedChargeType === "percentage"
												? ((Number(watchedPrice) * Number(watchedCharge)) / 100).toFixed(2)
												: Number(watchedCharge).toFixed(2)}{" "}
											={" "}
											৳
											{(
												Number(watchedPrice) +
												(watchedChargeType === "percentage"
													? (Number(watchedPrice) * Number(watchedCharge)) / 100
													: Number(watchedCharge))
											).toFixed(2)}
										</Typography>
									</Box>
								)}
							</Box>

							{/* Submit Action */}
							<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
								<Button
									size="small"
									variant="outlined"
									color="inherit"
									onClick={() =>
										reset({
											price: currentPrice,
											charge_type: currentChargeType,
											payment_gateway_charge: currentCharge,
										})
									}
									disabled={isPending}
									sx={{
										borderRadius: 1.5,
										textTransform: "none",
										fontWeight: 600,
										px: 2,
										py: 0.6,
									}}
								>
									Reset
								</Button>
								<LoadingButton
									type="button"
									onClick={handleSubmit(onSubmit)}
									variant="contained"
									color="primary"
									size="small"
									loading={isPending}
									startIcon={<CheckCircleTwoToneIcon fontSize="small" />}
									sx={{
										borderRadius: 1.5,
										textTransform: "none",
										fontWeight: 700,
										px: 2.5,
										py: 0.7,
										boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
									}}
								>
									<span>Save Configuration</span>
								</LoadingButton>
							</Box>
						</Box>
					</CardContent>
				</Card>
			</Container>
		</>
	);
};

export default TicketConfiguration;
